import React, { useState, useEffect, useRef } from "react";

import {
  Box,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import {
  useRecordContext,
  Button,
  useGetList,
  DateInput,
  NumberInput,
  Loading,
  BooleanInput,
  useGetOne,
  SelectInput,
  required,
  useNotify,
  useCreate,
  TextInput,
  useGetIdentity,
} from "react-admin";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  CellValueChangedEvent,
  RowSelectionModule,
  ModuleRegistry,
  AllCommunityModule,
  ClientSideRowModelModule,
  provideGlobalGridOptions,
} from "ag-grid-community";
import type { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import ScmAnalysesDialog from "./ScmDialogAnalyses";
import ScmHistoryDialog from "./ScmDialogHistory";
import ScmDialogAlert from "./ScmDialogAlert";
import "./style.css";

// AG Grid 33+: módulos precisam ser registrados explicitamente
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowSelectionModule,
  AllCommunityModule,
]);
provideGlobalGridOptions({ theme: "legacy" });

declare global {
  interface Window {
    aguardarFocoDetalhe?: (api: any) => void;
  }
}

/** ~8px por caractere + padding do header. Usado na grid de detalhe. */
const calcWidth = (title: string): number => title.length * 8 + 10;

/** minWidth da mestre: cabeçalho + folga, para o título não cortar em tela estreita. */
const minWidthMestre = (titulo: string, extra = 8): number =>
  calcWidth(titulo) + extra;

/**
 * Grid de detalhe: largura fixa por coluna (sem flex).
 * flex + sizeColumnsToFit espremiam o cabeçalho e abriam buraco antes do Quant.
 * O espaço livre em tela larga é aplicado depois, só na coluna Observação.
 */
const larguraFixaDetalhe = (col: ColDef): ColDef => {
  const {
    flex: _flex,
    suppressSizeToFit: _suppress,
    ...rest
  } = col as ColDef & {
    flex?: number;
    suppressSizeToFit?: boolean;
  };
  const field = String(rest.field || rest.colId || "");

  const porCampo: Record<string, number> = {
    emp: 44,
    nome_reduzido: 160,
    observacoes: 100,
    soma_emp: calcWidth("SOMA"),
    strexpo: calcWidth("EXPO"),
    cmmf: calcWidth("CMMF"),
    strsazo: calcWidth("Sazo"),
    strcamp: calcWidth("Camp"),
    qc: calcWidth("Quant"),
  };

  const porHeader = rest.headerName ? calcWidth(rest.headerName) : 0;
  const atual = typeof rest.width === "number" ? rest.width : 0;
  const minAtual = typeof rest.minWidth === "number" ? rest.minWidth : 0;
  const sugerido = porCampo[field] ?? 0;

  const minWidth = Math.max(minAtual, porHeader, sugerido, atual || 40);
  const width = Math.max(atual, sugerido, porHeader, minWidth);

  return { ...rest, width, minWidth };
};

/**
 * Tela estreita: cada coluna fica no mínimo (não corta; se não couber, scroll).
 * Tela larga: a sobra é repartida. Observação pesa mais; as outras ganham um pouco.
 * Usa o mínimo definido, não a largura atual, para um resize não gravar coluna espremida.
 */
const PESO_OBSERVACAO = 4;

const aplicarLargurasDetalhe = (api: GridApi, larguraGrid: number) => {
  if (!larguraGrid) return;

  const colunas = (api.getColumns() ?? []).filter((coluna) =>
    coluna.isVisible(),
  );
  if (!colunas.length) return;

  const itens = colunas.map((coluna) => ({
    id: coluna.getColId(),
    piso:
      coluna.getMinWidth() || (coluna.getColId() === "observacoes" ? 100 : 40),
    atual: coluna.getActualWidth(),
  }));

  const somaPisos = itens.reduce((acc, item) => acc + item.piso, 0);
  // 2px das bordas, para não abrir scroll à toa
  const sobra = larguraGrid - somaPisos - 2;
  const somaPesos = itens.reduce(
    (acc, item) => acc + (item.id === "observacoes" ? PESO_OBSERVACAO : 1),
    0,
  );

  let usado = 0;
  const ajustes: { key: string; newWidth: number }[] = [];

  itens.forEach((item, indice) => {
    const peso = item.id === "observacoes" ? PESO_OBSERVACAO : 1;
    const extra =
      sobra > 0 && indice < itens.length - 1
        ? Math.floor((sobra * peso) / somaPesos)
        : 0;
    usado += extra;
    const nova = item.piso + (sobra > 0 ? extra : 0);
    if (Math.abs(item.atual - nova) > 1) {
      ajustes.push({ key: item.id, newWidth: nova });
    }
  });

  // O arredondamento do floor fica na última coluna, para fechar a largura do grid.
  if (sobra > 0) {
    const ultima = itens[itens.length - 1];
    const nova = ultima.piso + (sobra - usado);
    const ja = ajustes.find((ajuste) => ajuste.key === ultima.id);
    if (ja) ja.newWidth = nova;
    else if (Math.abs(ultima.atual - nova) > 1) {
      ajustes.push({ key: ultima.id, newWidth: nova });
    }
  }

  if (ajustes.length) {
    api.setColumnWidths(ajustes);
  }
};

/** Largura via canvas (piso 60 / teto 200). Usada na grid de detalhe. */
const getColumnsWidth = (text: string): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return 60;
  const ajustado = context.measureText(text).width * 1.5 + 30;
  return Math.min(Math.max(ajustado, 60), 200);
};

type AlinhamentoColuna = "left" | "right" | "center";

const HEADER_CLASS: Record<AlinhamentoColuna, string> = {
  left: "ag-left-aligned-header",
  right: "ag-right-aligned-header",
  center: "ag-center-aligned-header",
};

const CELL_STYLE: Record<AlinhamentoColuna, Record<string, string>> = {
  left: { textAlign: "left", paddingLeft: "0.5px", paddingRight: "0px" },
  right: { textAlign: "right", paddingRight: "0px", paddingLeft: "0px" },
  center: { textAlign: "center" },
};

/**
 * Coluna da grid mestre.
 * flex = peso quando a tela é larga; minWidth = não corta o cabeçalho quando é estreita.
 */
const colMestre = (
  headerName: string,
  field: string,
  flex: number,
  alinhamento: AlinhamentoColuna = "left",
  extras: ColDef = {},
): ColDef => ({
  headerName,
  field,
  flex,
  minWidth: minWidthMestre(headerName),
  headerClass: HEADER_CLASS[alinhamento],
  cellStyle: CELL_STYLE[alinhamento],
  ...extras,
});

const formatarBRL = (params: { value?: number | null }) => {
  if (params.value == null) return "";
  return params.value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const formatarPercentual = (params: { value?: unknown }) => {
  const value = params.value;
  return value != null && value !== "" ? `${value}%` : "";
};

const estiloSituacao = (params: { value?: unknown }) => {
  const situacao = String(params.value);
  const cores: Record<string, { backgroundColor: string; color: string }> = {
    INDEF: { backgroundColor: "#fffda6", color: "black" },
    FALTA: { backgroundColor: "#eea499", color: "white" },
    OK: { backgroundColor: "#90e5af", color: "white" },
  };
  return {
    textAlign: "center" as const,
    ...(cores[situacao] ?? { backgroundColor: "lightgray", color: "black" }),
  };
};

/** Impede o mousedown de selecionar a linha (o AG Grid seleciona no mousedown e remonta a célula). */
const impedirSelecaoDaLinha = (event: React.MouseEvent) => {
  event.stopPropagation();
};

/** Ícones de ação da mestre: usa o SKU da linha, não o da seleção atual. */
const AcoesCellRenderer = (params: any) => {
  const sku = params.data?.codigo_sku ?? 0;
  return (
    <div onMouseDown={impedirSelecaoDaLinha} onClick={impedirSelecaoDaLinha}>
      <ScmAnalysesDialog sku={sku} />
      <ScmHistoryDialog sku={sku} />
    </div>
  );
};

export const ScmAnalysesInput = ({
  onSkuChange,
  atualizaCampoPedido,
  itemSelecionadoChange,
  qtdTotalItens,
  linhaSalva,
  onLinhaSalvaConsumida,
}: {
  onSkuChange?: (sku: number, info: any[]) => void;
  atualizaCampoPedido?: boolean;
  itemSelecionadoChange?: (itemId: number) => void;
  qtdTotalItens?: (itemId: number) => void;
  linhaSalva?: boolean;
  onLinhaSalvaConsumida?: () => void;
}) => {
  const [rowDados, setDados] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const record = useRecordContext();

  // Visibilidade de colunas da grid de detalhe (vêm dos filtros da análise)
  const [obs, setObs] = useState(record?.filtros_analise?.ver_observ);
  const [soma, setSoma] = useState(record?.filtros_analise?.ver_somas);
  const [formula, setFormula] = useState(record?.filtros_analise?.ver_formula);
  const [expoEsquerda, setExpoEsquerda] = useState(false);

  const [SKU, setSku] = useState(0);
  const [naoSalvo, setNaoSalvo] = useState(false);
  const [idItemSelect, setIdItemSelect] = useState(0);
  const [itens, setItens] = useState(() => record?.itens ?? []);
  const [showAlert, setShowAlert] = useState(false);
  const [ignoraAlert, setIgnoraAlert] = useState(false);
  // useRef: o atalho Alt+S precisa do valor atual sem esperar re-render
  const ignoraAlertRef = useRef(false);

  const [planejarFilter, setPlanejarFilter] = useState(
    record?.filtros_analise?.planejar ?? 1,
  );
  const [aplicaFiltro, setAplicaFiltro] = useState(true);
  const [situacaoRGB, setSituacao] = useState("");
  const [itemSelecionado, setItemSelecionado] = useState<number>(0);
  const [totalItens, setTotalItens] = useState(0);
  const linhaParaSelecionarRef = useRef<number | null>(null);
  const [totalMultiplo, setTotalMultiplo] = useState(0);

  const notify = useNotify();
  const [create] = useCreate();
  const [fazPostCompra, setFazPostCompra] = useState(false);
  const { identity } = useGetIdentity();

  const baseUrl = identity?.homologacao
    ? "https://sandbox.rnge.com.br/app/index.html#/"
    : "https://app.rnge.com.br/#/";

  useEffect(() => {
    if (itemSelecionadoChange) {
      itemSelecionadoChange(itemSelecionado);
    }
    if (qtdTotalItens) {
      qtdTotalItens(totalItens);
    }
    // Se clicar no botão Salvar Info, recebo um campo true, e com isso eu tenho a
    // certeza que a linha foi salva, aí posso setar o false no não salvo

    if (linhaSalva) {
      setNaoSalvo(false);
      onLinhaSalvaConsumida?.();
    }
  }, [itemSelecionado, totalItens, naoSalvo, linhaSalva]);

  const [total, setTotal] = useState(0);

  interface Item {
    quantidade?: number;
  }

  const calcularTotal = (dados: Item[]) => {
    const totalCalculado = dados.reduce(
      (acc: number, item: Item) => acc + (item.quantidade ?? 0),
      0,
    );
    setTotal(totalCalculado);
  };

  // Função que busca os dados com base nos filtros
  // Estado para armazenar os filtros
  const [filters, setFilters] = useState({
    id_analise: record?.id,
    codigo_sku: 0,
    dataini: record?.filtros_analise?.dataini,
    datafim: record?.filtros_analise?.datafim,
    planejar: planejarFilter,
    ic: parseInt(record?.filtros_analise?.ic),
  });

  const { data, isLoading } = useGetList(
    "scmestimates",
    {
      pagination: { page: 1, perPage: 9999 },
      sort: { field: "id", order: "ASC" },
      filter: filters,
    },
    { enabled: aplicaFiltro && !!record?.id },
  );

  // useGetOne para carregar os dados da coluna pedido
  const {} = useGetOne(
    "scmanalyses",
    { id: record?.id },
    { enabled: atualizaCampoPedido || fazPostCompra },
  );

  // Recarrega o item da mestre após salvar o campo Pedido
  const { data: LinhaDataItems } = useGetOne(
    "scmanalysisitems",
    { id: idItemSelect },
    { enabled: atualizaCampoPedido },
  );

  // Seta os dados que vem no record.item nessa constante
  useEffect(() => {
    if (record?.itens) {
      setItens(record.itens);
    }
  }, [record?.itens]);

  useEffect(() => {
    if (LinhaDataItems && idItemSelect != null) {
      linhaParaSelecionarRef.current = itemSelecionado; // setar corretamente
    }
  }, [LinhaDataItems, idItemSelect]);

  // Atualiza o rowData com os dados recebidos do useGetList
  useEffect(() => {
    if (data && data.length > 0) {
      setDados(data);
    }
    setLoading(isLoading); // Atualiza o estado de loading
  }, [data, isLoading]);

  // Evento disparado quando o grid carrega pela primeira vez
  const onFirstDataRendered = (params: any) => {
    const total = params.api.getDisplayedRowCount();
    setTotalItens(total);
  };

  const handleSelectionChange = (event: any) => {
    const api = event.api;
    const selectedNodes = api.getSelectedNodes();
    const selectedData = selectedNodes.map((node: any) => node.data);

    if (selectedData.length) {
      const selectedNode = selectedNodes[0];
      const rowIndex = selectedNode.rowIndex;

      // Mesma regra do Alt+S: com alteração não salva, bloqueia troca de linha (mouse)
      if (
        naoSalvo &&
        !ignoraAlertRef.current &&
        rowIndex != null &&
        rowIndex !== itemSelecionado
      ) {
        setShowAlert(true);
        api.getDisplayedRowAtIndex(itemSelecionado)?.setSelected(true);
        return;
      }

      // Atualiza os filtros com base na seleção
      const { codigo_sku, dataini, datafim, planejarFilter } = selectedData[0];

      setFilters((prev) => ({
        ...prev,
        id_analise: record?.id || 0,
        codigo_sku: codigo_sku || "",
        planejar: planejarFilter || prev.planejar,
        dataini: dataini || prev.dataini,
        datafim: datafim || prev.datafim,
        ic: !prev.ic ? record?.filtros_analise?.ic : prev.ic,
      }));

      setSku(codigo_sku);
      // Apenas 1 item é selecionado, por isso coloquei a posição 0 fixo
      setIdItemSelect(selectedData[0].id);
      setSituacao(selectedData[0]?.resultado?.situacao);

      // Efeitos adicionais que antes estavam no Alt+W/S
      api.ensureIndexVisible(rowIndex, "middle");
      setItemSelecionado(rowIndex);
    }
  };

  // Marca true para que possa chamar o filtro
  const handleAplicaFiltro = () => {
    setAplicaFiltro(true);
  };

  const handleFilterChange = (e: any) => {
    const { name, value } = e.target;

    // Mapeia os nomes vindos do input para os nomes esperados pela API
    const nameMap: { [key: string]: string } = {
      "filtros_analise.planejar": "planejar",
      "filtros_analise.dataini": "dataini",
      "filtros_analise.datafim": "datafim",
      "filtros_analise.ic": "ic",
    };

    const customName = nameMap[name] || name;

    if (customName === "planejar") {
      setPlanejarFilter(value);
    }

    setAplicaFiltro(false);
    setFilters((prevFilters) => ({
      ...prevFilters,
      [customName]:
        customName === "ic" || customName === "filtros.ic"
          ? value === ""
            ? null
            : Number(value)
          : value,
    }));
  };

  const handleFormulaChange = (e: any) => {
    setFormula(e.target.checked);
  };

  const handleObsChange = (e: any) => {
    setObs(e.target.checked);
  };

  const handleSomaChange = (e: any) => {
    setSoma(e.target.checked);
  };

  const handleExpoEsquerdaChange = (e: any) => {
    setExpoEsquerda(e.target.checked);
  };

  // Controle do acordeon
  const [expanded, setExpanded] = useState(false);

  const handleChange = () => {
    setExpanded((prev) => !prev);
  };

  const [topHeight, setTopHeight] = useState(100);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(150);
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseMove = (event: MouseEvent) => {
    if (!isResizing) return;

    const delta = event.clientY - startY;
    const newHeight = Math.max(0, startHeight + delta);

    setTopHeight(newHeight);
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsResizing(true);
    setStartY(event.clientY);
    setStartHeight(topHeight);
  };

  // Adiciona e remove os eventos globalmente quando necessário
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  type RowDataType = {
    id?: string;
    Canal?: string;
    Custo?: string;
    Preço?: string;
    "MC Preço"?: string;
    ER?: string;
    EV?: string;
    s12?: string;
    s11?: string;
    s10?: string;
    s9?: string;
    s8?: string;
    s7?: string;
    s6?: string;
    s5?: string;
    s4?: string;
    s3?: string;
    s2?: string;
    s1?: string;
    Total?: string;
    unid?: string;
    Descricao?: string;
    semanas?: [];
  };

  // Componente para LINK
  const SkuLinkRenderer = (props: any) => {
    const sku = props.value;
    return (
      <a
        href={`${baseUrl}products/${sku}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {sku}
      </a>
    );
  };

  // Setar linha
  const onRowDataUpdated = () => {
    const index = linhaParaSelecionarRef.current;
    if (index != null && mestreGridRef.current) {
      const mestreApi = mestreGridRef.current.api;

      mestreApi.getDisplayedRowAtIndex(index)?.setSelected(true);
      mestreApi.ensureIndexVisible(index, "middle");

      setFazPostCompra(false);
      linhaParaSelecionarRef.current = null;
    }
  };

  const rowData: RowDataType[] = rowDados || [];

  // Quando houver alteração, prepara o POST
  const atualizouCelula = (ev: CellValueChangedEvent) => {
    // rowIndex de linha fixa conta a partir de 0 dentro das fixas, então não
    // serve como índice em rowDados. Localiza pelo id, que é único no grid.
    const indice = rowDados.findIndex((item) => item.id === ev.data?.id);

    if (indice !== -1) {
      rowDados[indice] = { ...ev.data };
      setDados([...rowDados]);
      onSkuChange?.(SKU, rowDados);

      const linhaCompra = ev?.data?.id;

      if (linhaCompra !== 9999) {
        setNaoSalvo(true);
      }

      if (linhaCompra === 9999 && ev?.column?.getColDef()?.field === "qc") {
        const payload = {
          id_analise: record?.id,
          codigo_sku: SKU,
          info_sku: [ev?.data],
        };

        create(
          "scmestimates",
          { data: payload },
          {
            onSuccess: (response: any) => {
              linhaParaSelecionarRef.current = itemSelecionado;
              setFazPostCompra(true);
            },
            onError: (error: any) => {
              notify(error.message, { type: "error" });
            },
          },
        );
      }
      calcularTotal(rowDados);
    }
  };

  const getColumnDefs = (data: RowDataType[]) => {
    if (!data || data.length === 0) return [];

    // Definição de colunas fixas
    const fixedColumnsIni = [
      {
        headerName: "",
        field: "emp",
        headerClass: "ag-left-aligned-header",
        suppressSizeToFit: true,
        pinned: "left" as "left",
        flex: 1,
        lockPinned: true,
      },
      {
        headerName: "Nome Empresa",
        field: "nome_reduzido",
        cellStyle: (params: any) => ({
          textAlign: "left",
          paddingLeft: "0px",
          fontWeight:
            params?.data?.nome_reduzido?.startsWith("TOTAL") ||
            params?.data?.nome_reduzido === "Compra"
              ? "bold"
              : "normal",
        }),
        headerClass: "ag-left-aligned-header",
        pinned: "left" as "left",
        lockPinned: true,
        tooltipField: "nome_reduzido",
      },
      ...(expoEsquerda
        ? [
            {
              field: "strexpo",
              headerClass: "ag-right-aligned-header",
              pinned: "left" as "left",
              lockPinned: true,
              headerComponent: () => (
                <span
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  E<u>X</u>PO
                </span>
              ),
              // width: calcWidth("EXPO"),
              flex: 1,
              width: calcWidth("EXPO"),
              editable: (params: any) => {
                return !params.data?.nome_reduzido?.startsWith("TOTAL");
              },
              // Para o total
              valueGetter: (params: any) => {
                // Se for a última linha e a sigla_loja for "Compra", apenas exibe o valor, sem somar
                if (params?.data?.nome_reduzido?.startsWith("Compra")) {
                  return params.data?.strexpo ?? 0;
                }
                if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
                  return rowDados.reduce((acc, item) => {
                    // Ignora a linha "Compra" na soma
                    if (item.nome_reduzido !== "Compra") {
                      return (
                        acc +
                        Number(
                          parseFloat(item.strexpo?.replace(",", ".") || "0"),
                        )
                      );
                    }
                    return acc; // Caso a sigla seja "Compra", não adiciona na soma
                  }, 0);
                }
                return Number(params.data?.strexpo ?? 0);
              },
              cellStyle: (params: any) => ({
                textAlign: "right",
                paddingRight: "5px",
                fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
                  ? "bold"
                  : "normal",
              }),
            },
          ]
        : []),
      // Condicionail Fórmula
      ...(formula
        ? [
            {
              headerName: "ESB",
              field: "es_bruto",
              headerClass: "ag-right-aligned-header",
              cellStyle: {
                textAlign: "right",
                // paddingRight: "0px",
              },
              pinned: "left" as "left",
              flex: 1,
              width: getColumnsWidth("ESB"),
              valueFormatter: (params: any) =>
                typeof params.value === "number" ? params.value.toFixed(2) : "",
            },
            {
              headerName: "ES",
              field: "es",
              headerClass: "ag-right-aligned-header",
              cellStyle: {
                textAlign: "right",
                // paddingRight: "0px",
              },
              aggFunc: "sum",
              pinned: "left" as "left",
              flex: 1,
              width: getColumnsWidth("ES"),
              // Para o total
              valueGetter: (params: any) => {
                if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
                  return rowDados.reduce(
                    (acc, item) => acc + Number(item.es || 0),
                    0,
                  );
                }
                return Number(params.data?.es ?? 0);
              },
            },
            {
              headerName: "NR",
              field: "nr",
              headerClass: "ag-right-aligned-header",
              cellStyle: {
                textAlign: "right",
                // paddingRight: "0px",
              },
              pinned: "left" as "left",
              width: getColumnsWidth("NR"),
              flex: 1,
              // Para o total
              valueGetter: (params: any) => {
                if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
                  return rowDados.reduce(
                    (acc, item) => acc + Number(item.nr || 0),
                    0,
                  );
                }
                return Number(params.data?.nr ?? 0);
              },
            },
            {
              headerName: "EM",
              field: "em",
              headerClass: "ag-right-aligned-header",
              cellStyle: {
                textAlign: "right",
              },
              pinned: "left" as "left",
              width: getColumnsWidth("EM"),
              flex: 1,
            },
          ]
        : []),
      {
        field: "curva",
        headerClass: "ag-right-aligned-header",
        lockPinned: true,
        flex: 1,
        width: calcWidth("0000"),
        cellStyle: (params: any) => ({
          textAlign: "right",
        }),
        pinned: "left" as "left",
      },
      {
        field: "dce",
        headerName: "DCE",
        headerClass: "ag-right-aligned-header",
        lockPinned: true,
        flex: 1,
        width: calcWidth("000"),
        cellStyle: (params: any) => ({
          textAlign: "right",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",
        }),
        pinned: "left" as "left",
      },

      {
        field: "er",
        headerName: "ER",
        headerClass: "ag-right-aligned-header",
        cellStyle: (params: any) => ({
          textAlign: "right",
          color: params?.data?.er < 0 ? "red" : "blue",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",
        }),
        pinned: "left" as "left",
        // lockPinned: true,
        // suppressSizeToFit: true, // Impede que o ag-Grid altere esse tamanho
        flex: 1,
        width: calcWidth("00000"),
        valueSetter: (params: any) => {
          const newValue = params.newValue
            ? parseInt(params.newValue, 10)
            : undefined;

          if (newValue !== undefined && !isNaN(newValue)) {
            params.data.er = newValue;
            return true;
          }
          return false; // Impede a atualização caso o valor não seja válido
        },
        // Para o total
        valueGetter: (params: any) => {
          if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
            return rowDados.reduce(
              (acc, item) => acc + Number(item.er || 0),
              0,
            );
          }
          return Number(params.data?.er ?? 0);
        },
      },
      // Condicional
      ...(formula
        ? [
            {
              headerName: "EP",
              field: "ep",
              pinned: "left" as "left",
              width: getColumnsWidth("EP"),
            },
          ]
        : []),
      {
        headerName: "##",
        field: "etmp",
        pinned: "left" as "left",
        headerClass: "ag-right-aligned-header",
        flex: 1,
        width: calcWidth("00000"),
        lockPinned: true,
        cellStyle: (params: any) => ({
          textAlign: "right",
          paddingRight: "5px",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",
        }),
        // Para o total
        valueGetter: (params: any) => {
          if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
            return rowDados.reduce(
              (acc, item) => acc + Number(item.etmp || 0),
              0,
            );
          }
          return Number(params.data?.etmp ?? 0);
        },
      },
      {
        field: "ocnf",
        headerName: "OC NF",
        pinned: "left" as "left",
        headerClass: "ag-right-aligned-header",
        lockPinned: true,
        flex: 1,
        width: calcWidth("00000"),
        cellStyle: (params: any) => ({
          textAlign: "right",
          paddingRight: "5px",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",
        }),
        // Para o total
        valueGetter: (params: any) => {
          if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
            return rowDados.reduce(
              (acc, item) => acc + Number(item.ocnf || 0),
              0,
            );
          }
          return Number(params.data?.ocnf ?? 0);
        },
      },
      {
        field: "ev2",
        headerName: "EV",
        headerClass: "ag-right-aligned-header",
        cellClass: "ag-cell-space ev-column-cell", // Classe extra só para essa célula
        colId: "ev-column", // ID único para esta coluna
        cellStyle: (params: any) => ({
          textAlign: "right",
          paddingRight: "5px",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",
        }),
        pinned: "left" as "left",
        lockPinned: true,
        flex: 1,
        width: calcWidth("00000"),
        valueSetter: (params: any) => {
          const newValue = params.newValue
            ? parseInt(params.newValue, 10)
            : undefined;

          if (newValue !== undefined && !isNaN(newValue)) {
            params.data.ev = newValue;
            return true;
          }
          return false; // Impede a atualização caso o valor não seja válido
        },
        // Para o total
        valueGetter: (params: any) => {
          if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
            return rowDados.reduce(
              (acc, item) => acc + Number(item.ev2 || 0),
              0,
            );
          }
          return Number(params.data?.ev2 ?? 0);
        },
      },
      // Condicional
      ...(formula
        ? [
            {
              field: "dfx2",
              headerName: "Dfx2",
              headerClass: "ag-right-aligned-header",
              pinned: "left" as "left",
              lockPinned: true,
              cellStyle: {
                textAlign: "right",
              },
              width: getColumnsWidth("dfx2"),
            },
            {
              field: "dnr",
              headerName: "DNR",
              cellStyle: { textAlign: "right" },
              headerClass: "ag-right-aligned-header",
              pinned: "left" as "left",
              lockPinned: true,
              width: getColumnsWidth("dnr"),
            },
          ]
        : []),
      //
    ];

    const fixedColumnsFin = [
      ...(soma
        ? [
            {
              headerName: "SOMA",
              field: "soma_emp",
              cellStyle: () => ({
                textAlign: "right",
                fontWeight: "bold",
              }),
              headerClass: "ag-right-aligned-header",
              pinned: "right" as "right",
              flex: 1,
              width: calcWidth("SOMA") + 24,
            },
            {
              headerName: "%",
              field: "perc_soma",
              cellStyle: { textAlign: "right" },
              pinned: "right" as "right",
              headerClass: "ag-right-aligned-header ev-column-header",
              flex: 1,
              valueFormatter: (params: any) => {
                const value = params.value;
                return value != null && value !== "" ? `${value}%` : "";
              },
              width: calcWidth("SOMA"),
            },
          ]
        : []),
      // Condicional
      ...(obs
        ? [
            {
              field: "observacoes",
              headerName: "Observ.",
              pinned: "right" as "right",
              // width: calcWidth("observacoes"),
              headerClass: "ag-left-aligned-header",
              flex: 2,
              resizable: true,
              editable: (params: any) => {
                return !params.data?.nome_reduzido?.startsWith("TOTAL");
              },
            },
          ]
        : []),
      {
        field: "cmm",
        headerName: "CMM",
        lockPinned: true,
        cellStyle: (params: any) => ({
          textAlign: "right",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",
          color: params?.data?.dce_baixo ? "red" : "normal",
        }),
        pinned: "right" as "right",
        headerClass: "ag-right-aligned-header",
        flex: 1,
        width: calcWidth("000000"),
        valueSetter: (params: any) => {
          const newValue = params.newValue
            ? parseInt(params.newValue, 10)
            : undefined;

          if (newValue !== undefined && !isNaN(newValue)) {
            params.data.cmm = newValue;
            return true; // Indica que o valor foi atualizado
          }
          return false; // Impede a atualização caso o valor não seja válido
        },
        // Para o total
        valueGetter: (params: any) => {
          if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
            return rowDados.reduce(
              (acc, item) => acc + Number(item.cmm || 0),
              0,
            );
          }
          return Number(params.data?.cmm ?? 0);
        },
        // Formatando por conta das casas decimais
        valueFormatter: (params: any) => {
          const value = params.value;
          return typeof value === "number" ? value.toFixed(2) : "";
        },
      },
      {
        field: "esmin",
        headerName: "ESMin",
        pinned: "right" as "right",
        headerClass: "ag-right-aligned-header",
        cellStyle: (params: any) => ({
          textAlign: "right",
          paddingRight: "5px",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",
        }),
        flex: 1,
        width: calcWidth("00000"),
        lockPinned: true,
        //
        valueSetter: (params: any) => {
          const newValue = params.newValue
            ? parseInt(params.newValue, 10)
            : undefined;

          if (newValue !== undefined && !isNaN(newValue)) {
            params.data.esmin = newValue;
            return true; // Indica que o valor foi atualizado
          }
          return false; // Impede a atualização caso o valor não seja válido
        },
        // Para o total
        valueGetter: (params: any) => {
          if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
            return rowDados.reduce(
              (acc, item) => acc + Number(item.esmin || 0),
              0,
            );
          }
          return Number(params.data?.esmin ?? 0);
        },
      },
      // Apenas as 5 últimas colunas são editáveis
      {
        // headerName: "C[M]MF",
        field: "cmmf",
        headerClass: "ag-right-aligned-header",
        lockPinned: true,
        headerComponent: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            C<u>M</u>MF
          </span>
        ),
        cellStyle: (params: any) => ({
          textAlign: "right",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",
        }),
        flex: 1,
        width: calcWidth("00000"),
        pinned: "right" as "right",
        editable: (params: any) => {
          return !params.data?.nome_reduzido?.startsWith("TOTAL");
        },
        valueSetter: (params: any) => {
          const newValue = params.newValue
            ? parseInt(params.newValue, 10)
            : undefined;

          if (newValue !== undefined && !isNaN(newValue)) {
            params.data.cmmf = newValue;
            return true; // Indica que o valor foi atualizado
          }
          return false; // Impede a atualização caso o valor não seja válido
        },
        // Para o total
        valueGetter: (params: any) => {
          if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
            const total = rowDados.reduce(
              (acc, item) => acc + Number(item.cmmf || 0),
              0,
            );
            return parseFloat(total.toFixed(2));
          }
          return parseFloat(Number(params.data?.cmmf ?? 0).toFixed(2));
        },
      },
      {
        field: "strsazo",
        lockPinned: true,
        headerClass: "ag-right-aligned-header",
        headerComponent: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            {/* SA<u>Z</u>O */}
            Sa<u>z</u>o
          </span>
        ),
        flex: 1,
        width: calcWidth("00000"),
        cellStyle: (params: any) => ({
          textAlign: "right",
          paddingRight: "0px",
          color: params?.data?.er < 0 ? "red" : "blue",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",
        }),
        pinned: "right" as "right",
        editable: (params: any) => {
          return !params.data?.nome_reduzido?.startsWith("TOTAL");
        },
        valueSetter: (params: any) => {
          const newValue = params.newValue
            ? // ? parseInt(params.newValue, 10)
              params.newValue
            : undefined;

          if (newValue !== undefined && !isNaN(newValue)) {
            params.data.strsazo = newValue;
            return true; // Indica que o valor foi atualizado
          }
          return false; // Impede a atualização caso o valor não seja válido
        },
        // Para o total
        valueGetter: (params: any) => {
          if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
            return rowDados.reduce(
              (acc, item) =>
                item?.nome_reduzido?.includes("Compra")
                  ? acc
                  : acc + (parseInt(item.strsazo) || 0),
              0,
            );
          }

          return params?.data?.strsazo || 0;
        },
      },
      {
        field: "strcamp",
        lockPinned: true,
        headerClass: "ag-right-aligned-header",
        headerComponent: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <u>C</u>amp
          </span>
        ),
        cellStyle: (params: any) => ({
          textAlign: "right",
          paddingRight: "0px",
          color: params?.data?.er < 0 ? "red" : "blue",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",
        }),
        pinned: "right" as "right",
        editable: (params: any) => {
          return !params.data?.nome_reduzido?.startsWith("TOTAL");
        },
        // Para o total
        valueGetter: (params: any) => {
          // Se for a última linha e a sigla_loja for "Compra", apenas exibe o valor, sem somar
          if (params?.data?.nome_reduzido?.startsWith("Compra")) {
            return params.data?.strcamp ?? 0;
          }
          if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
            return rowDados.reduce((acc, item) => {
              // Ignora a linha "Compra" na soma
              if (!item.nome_reduzido?.startsWith("Compra")) {
                return (
                  acc +
                  Number(parseFloat(item.strcamp?.replace(",", ".") || "0"))
                );
              }
              return acc; // Caso a sigla seja "Compra", não adiciona na soma
            }, 0);
          }
          return Number(params.data?.strcamp ?? 0);
        },
        flex: 1,
        width: calcWidth("00000"),
      },
      ...(!expoEsquerda
        ? [
            {
              field: "strexpo",
              headerClass: "ag-right-aligned-header",
              pinned: "right" as "right",
              lockPinned: true,
              headerComponent: () => (
                <span
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  E<u>x</u>po
                </span>
              ),
              // width: calcWidth("EXPO"),
              flex: 1,
              width: calcWidth("EXPO"),
              editable: (params: any) => {
                return !params.data?.nome_reduzido?.startsWith("TOTAL");
              },
              // Para o total
              valueGetter: (params: any) => {
                // Se for a última linha e a sigla_loja for "Compra", apenas exibe o valor, sem somar
                if (params?.data?.nome_reduzido?.startsWith("Compra")) {
                  return params.data?.strexpo ?? 0;
                }
                if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
                  return rowDados.reduce((acc, item) => {
                    // Ignora a linha "Compra" na soma
                    if (item.nome_reduzido !== "Compra") {
                      return (
                        acc +
                        Number(
                          parseFloat(item.strexpo?.replace(",", ".") || "0"),
                        )
                      );
                    }
                    return acc; // Caso a sigla seja "Compra", não adiciona na soma
                  }, 0);
                }
                return Number(params.data?.strexpo ?? 0);
              },
              cellStyle: (params: any) => ({
                textAlign: "right",
                paddingRight: "5px",
                fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
                  ? "bold"
                  : "normal",
              }),
            },
          ]
        : []),
      {
        field: "qc",
        lockPinned: true,
        headerComponent: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <u>Q</u>uant
          </span>
        ),
        cellStyle: (params: any) => {
          const isCompra = params?.data?.nome_reduzido?.startsWith("Compra");
          const isTotal = params?.data?.nome_reduzido?.startsWith("TOTAL");
          const qc = Number(params?.value);

          // Extrai o número do "strsazo", ex: de "10 KG" pega 10
          const strsazoRaw = params?.data?.strsazo || "";
          const match = strsazoRaw.match(/\d+(\.\d+)?/); // pega número inteiro ou decimal
          const strsazo = match ? Number(match[0]) : NaN;

          let backgroundColor = "";

          // console.log("total", total);

          if (isCompra && !isNaN(strsazo) && strsazo !== 0) {
            backgroundColor = qc % strsazo === 0 ? "yellow" : "red";
          } else if (isTotal) {
            if (situacaoRGB === "FALTA") {
              backgroundColor = "#eea499";
            } else if (situacaoRGB === "INDEF") {
              backgroundColor = "#fffda6";
            } else if (situacaoRGB === "OK") {
              backgroundColor = "#90e5af";
            }
          }

          // Regra para cor:
          let color = "red";

          if (qc > 0) {
            color = "blue";
          } else if (isTotal && totalMultiplo > 0) {
            color = "blue";
          }

          return {
            textAlign: "right",
            fontWeight: isTotal ? "bold" : "normal",
            background: backgroundColor,
            color,
          };
        },
        pinned: "right" as "right",
        // editable: true,
        editable: (params: any) => {
          return !params.data?.nome_reduzido?.startsWith("TOTAL");
        },
        valueSetter: (params: any) => {
          const newValue = params.newValue
            ? parseInt(params.newValue, 10)
            : undefined;

          if (newValue !== undefined && !isNaN(newValue)) {
            params.data.qc = newValue;
            return true; // Indica que o valor foi atualizado
          }
          return false; // Impede a atualização caso o valor não seja válido
        },
        // Para o total
        valueGetter: (params: any) => {
          if (params?.data?.nome_reduzido?.startsWith("TOTAL")) {
            const total = rowDados
              .filter((item) => !item.nome_reduzido?.startsWith("Compra"))
              .reduce((acc, item) => acc + Number(item.qc || 0), 0);

            // Pega a linha da "Compra"
            const linhaCompra = rowDados.find((item) =>
              item.nome_reduzido?.startsWith("Compra"),
            );
            const strSazo = linhaCompra?.strsazo || "";

            // Extrai o número do strsazo (ex: "18 UN" → 18)
            const match = strSazo.match(/\d+/);
            const base = match ? parseInt(match[0], 10) : 1;

            // Calcula o próximo múltiplo de base que seja >= total
            const proximoMultiplo = Math.ceil(total / base) * base;
            setTotalMultiplo(proximoMultiplo);

            return proximoMultiplo;
          }

          return Number(params.data?.qc ?? 0);
        },
        headerClass: "ag-right-aligned-header",
        cellClass: "ag-cell-space ev-column-cell", // Classe extra só para essa célula
        flex: 1,
        width: calcWidth("00000"),
        // suppressSizeToFit: true,
      },
    ];

    const semanasMap = new Map<string, string>();

    data.forEach((row) => {
      row?.semanas?.forEach((semana: any) => {
        if (semana?.num && semana?.dia) {
          semanasMap.set(semana.num, semana.dia);
        }
      });
    });

    const dynamicColumns = Array.from(semanasMap.entries())
      .sort(([numA], [numB]) => Number(numA) - Number(numB))
      .map(([num, dia]) => ({
        colId: `semana_${num}`, // 🔴 MUITO IMPORTANTE
        headerName: dia, // mostra "17/03" ou o que vier no campo .dia
        field: `semana_${dia}`,
        headerClass: "ag-right-aligned-header",
        cellStyle: (params: any) => ({
          textAlign: "right",
          paddingRight: "1px",
          paddingLeft: "0px",
          fontWeight: params?.data?.nome_reduzido?.startsWith("TOTAL")
            ? "bold"
            : "normal",

          color:
            // params?.data?.nome_reduzido?.startsWith("TOTAL") ||
            params?.data?.nome_reduzido === "Compra" ? "red" : "normal",
        }),
        width: calcWidth(dia),
        minWidth: calcWidth(dia),
      }));

    // Sem flex: a soma das larguras pode passar da tela → scroll horizontal, sem cortar.
    return [...fixedColumnsIni, ...dynamicColumns, ...fixedColumnsFin].map(
      larguraFixaDetalhe,
    );
  };

  // Transformar os dados para que o grid reconheça os valores das semanas
  const transformRowData = (data: RowDataType[]) => {
    return data.map((row) => {
      const semanasData = row?.semanas?.reduce((acc: any, semana: any) => {
        acc[`semana_${semana.dia}`] = semana.qtd;
        return acc;
      }, {});
      return { ...row, ...semanasData };
    });
  };

  const columnDefs = getColumnDefs(rowData);
  const transformedRowData = transformRowData(rowData);
  const linhaFixa = (nome?: string) =>
    !!nome?.startsWith("TOTAL") || !!nome?.startsWith("Compra");
  const linhasFixas = transformedRowData.filter((row) =>
    linhaFixa(row?.nome_reduzido),
  );
  const linhasEmpresa = transformedRowData.filter(
    (row) => !linhaFixa(row?.nome_reduzido),
  );

  // Controle de Foco
  const [focusedGrid, setFocusedGrid] = useState<"mestre" | "detalhe" | null>(
    null,
  );

  const mestreGridRef = useRef<AgGridReact>(null);
  const detalheGridRef = useRef<AgGridReact>(null);
  const detalheBoxRef = useRef<HTMLDivElement>(null);
  const larguraDetalheRef = useRef(0);

  // Reaplica depois do render: o columnDefs novo devolve a Observação ao mínimo
  // e, no resize, o vão voltava. O efeito roda após o grid aplicar as colunas.
  useEffect(() => {
    const api = detalheGridRef.current?.api;
    if (!api) return;
    const frame = requestAnimationFrame(() => {
      aplicarLargurasDetalhe(
        api,
        detalheBoxRef.current?.clientWidth || larguraDetalheRef.current,
      );
    });
    return () => cancelAnimationFrame(frame);
  });

  // Flag para garantir que o grid de detalhe está pronto (atalhos Alt+C/M/Z/Q/X)
  const [detalheGridReady, setDetalheGridReady] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!mestreGridRef.current || !detalheGridRef.current) return;

      // Navegação no grid mestre com Alt + W / Alt + S
      const mestreApi = mestreGridRef.current.api;
      const selectedNodesMestre = mestreApi.getSelectedNodes();
      let rowIndexMestre = selectedNodesMestre.length
        ? selectedNodesMestre[0].rowIndex
        : 0;

      // Pressionar Alt + W para subir
      if (event.altKey && event.key.toLowerCase() === "w") {
        if (naoSalvo && !ignoraAlertRef.current) {
          setShowAlert(true);
        } else {
          event.preventDefault();
          if (rowIndexMestre !== null && rowIndexMestre > 0) {
            rowIndexMestre -= 1;
            mestreApi.getDisplayedRowAtIndex(rowIndexMestre)?.setSelected(true);
            mestreApi.ensureIndexVisible(rowIndexMestre, "middle");
            setItemSelecionado(rowIndexMestre);
          }
        }
      }

      // Pressionar Alt + S para descer
      if (event.altKey && event.key.toLowerCase() === "s") {
        if (naoSalvo && !ignoraAlertRef.current) {
          setShowAlert(true);
        } else {
          event.preventDefault();
          if (
            rowIndexMestre !== null &&
            rowIndexMestre < mestreApi.getDisplayedRowCount() - 1
          ) {
            rowIndexMestre += 1;
            mestreApi.getDisplayedRowAtIndex(rowIndexMestre)?.setSelected(true);
            mestreApi.ensureIndexVisible(rowIndexMestre, "middle");
            setItemSelecionado(rowIndexMestre);
          }
        }
      }

      // Alt + C Camp
      if (event.altKey && event.key.toLowerCase() === "c") {
        event.preventDefault();

        if (detalheGridReady) {
          const detalheApi = detalheGridRef.current.api;
          const firstRow = detalheApi.getDisplayedRowAtIndex(0);
          if (firstRow) {
            detalheApi.setFocusedCell(0, "strcamp");
            detalheApi.startEditingCell({
              rowIndex: 0,
              colKey: "strcamp",
            });
          }
        }
      }

      // Alt + M CMMF
      if (event.altKey && event.key.toLowerCase() === "m") {
        event.preventDefault();

        if (detalheGridReady) {
          const detalheApi = detalheGridRef.current.api;
          const firstRow = detalheApi.getDisplayedRowAtIndex(0);
          if (firstRow) {
            detalheApi.setFocusedCell(0, "cmmf");
            detalheApi.startEditingCell({
              rowIndex: 0,
              colKey: "cmmf",
            });
          }
        }
      }

      // Alt + Z Sazo
      if (event.altKey && event.key.toLowerCase() === "z") {
        event.preventDefault();

        if (detalheGridReady) {
          const detalheApi = detalheGridRef.current.api;
          const firstRow = detalheApi.getDisplayedRowAtIndex(0);
          if (firstRow) {
            detalheApi.setFocusedCell(0, "strsazo");
            detalheApi.startEditingCell({
              rowIndex: 0,
              colKey: "strsazo",
            });
          }
        }
      }

      // Alt + Q Quant
      if (event.altKey && event.key.toLowerCase() === "q") {
        event.preventDefault();

        if (detalheGridReady) {
          const detalheApi = detalheGridRef.current.api;
          const firstRow = detalheApi.getDisplayedRowAtIndex(0);
          if (firstRow) {
            detalheApi.setFocusedCell(0, "qc");
            detalheApi.startEditingCell({
              rowIndex: 0,
              colKey: "qc",
            });
          }
        }
      }

      // Alt + X Expo
      if (event.altKey && event.key.toLowerCase() === "x") {
        event.preventDefault();

        if (detalheGridReady) {
          const detalheApi = detalheGridRef.current.api;
          const firstRow = detalheApi.getDisplayedRowAtIndex(0);
          if (firstRow) {
            detalheApi.setFocusedCell(0, "strexpo");
            detalheApi.startEditingCell({
              rowIndex: 0,
              colKey: "strexpo",
            });
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [detalheGridReady, naoSalvo]);

  const onDetalheGridReady = () => {
    setDetalheGridReady(true);
  };

  return (
    <Stack gap={1} p={1} sx={{ width: "100%" }}>
      <div>
        <div>
          <Accordion expanded={expanded} onChange={handleChange}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1" component="div">
                Análise {record?.id}
                {":"}
                {record?.desc_analise ? record?.desc_analise : ""}
              </Typography>
            </AccordionSummary>
            <AccordionDetails style={{ paddingTop: 0, paddingBottom: 0 }}>
              <Box display={{ xs: "block", sm: "flex" }}>
                <Box flex={1} mr={{ sm: "0.5em" }}>
                  <Box display={{ xs: "block", sm: "flex" }}>
                    <Box flex={1} mr={{ sm: "0.5em" }}>
                      <DateInput
                        source="filtros_analise.dataini"
                        label="Data Inicial"
                        onChange={handleFilterChange}
                      />
                    </Box>
                    <Box flex={1} mr={{ sm: "0.5em" }}>
                      <DateInput
                        source="filtros_analise.datafim"
                        label="Data Final"
                        onChange={handleFilterChange}
                      />
                    </Box>
                    <Box flex={1} mr={{ sm: "0.5em" }}>
                      <NumberInput
                        source="filtros_analise.ic"
                        defaultValue={record?.filtros_analise?.ic}
                        onChange={handleFilterChange}
                        placeholder="1"
                        // Redutor. Vai vir da API
                        helperText="IC Padrão =  1"
                        label="IC"
                        InputProps={{
                          sx: {
                            input: {
                              textAlign: "right",
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box flex={1} mr={{ sm: "0.5em" }}>
                      <SelectInput
                        source="filtros_analise.planejar"
                        label="Planejar"
                        defaultValue={record?.filtros_analise?.planejar ?? 1}
                        InputLabelProps={{ shrink: true }}
                        validate={required()}
                        onChange={handleFilterChange}
                        choices={[
                          { id: 1, name: "Ordinária" },
                          { id: 2, name: "Sazo + Ordinária" },
                          { id: 3, name: "Somente SAZO" },
                        ]}
                      />
                    </Box>
                    <Box
                      flex={0.8}
                      mr={{ sm: "0.5em" }}
                      style={{ minWidth: "40px" }}
                    >
                      <TextInput
                        source="filtros_analise.tr"
                        label="TR"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { textAlign: "right" } }}
                        disabled
                      />
                    </Box>
                    <Box flex={1} mr={{ sm: "0.5em" }}>
                      <Button
                        onClick={handleAplicaFiltro}
                        label="Filtrar"
                        sx={{
                          marginTop: 2,
                        }}
                      >
                        <SearchIcon />
                      </Button>
                    </Box>
                  </Box>
                </Box>
                <Box mr={{ sm: "0.5em" }} flex={1.5}>
                  <Box
                    display={{ xs: "block", sm: "flex" }}
                    justifyContent="flex-end"
                  >
                    <Box mr={{ sm: "0.5em" }}>
                      <BooleanInput
                        // source="formula"
                        source="filtros_analise.ver_formula"
                        label="Fórmula"
                        onChange={handleFormulaChange}
                        sx={{ marginTop: "1.4em" }}
                      />
                    </Box>
                    <Box mr={{ sm: "0.5em" }}>
                      <BooleanInput
                        source="filtros_analise.ver_observ"
                        label="Observações"
                        onChange={handleObsChange}
                        sx={{ marginTop: "1.4em" }}
                      />
                    </Box>
                    <Box mr={{ sm: "0.5em" }}>
                      <BooleanInput
                        source="filtros_analise.ver_somas"
                        label="Soma/%"
                        onChange={handleSomaChange}
                        sx={{ marginTop: "1.4em" }}
                      />
                    </Box>
                    <Box mr={{ sm: "0.5em" }}>
                      <BooleanInput
                        source="filtros_analise.expo_esquerda"
                        label="EXPO na Esquerda"
                        onChange={handleExpoEsquerdaChange}
                        sx={{ marginTop: "1.4em" }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </div>
        <div
          className="ag-theme-alpine grid-mestre no-row-hover"
          style={{ height: `${topHeight}px` }}
          onFocus={() => setFocusedGrid("mestre")}
          translate="no"
        >
          <AgGridReact
            ref={mestreGridRef}
            modules={[ClientSideRowModelModule]}
            onRowDataUpdated={onRowDataUpdated}
            onSelectionChanged={handleSelectionChange}
            suppressHorizontalScroll={false}
            tooltipShowDelay={400}
            defaultColDef={{ resizable: true }}
            onGridReady={(params) => {
              onDetalheGridReady();
              setTimeout(() => {
                params.api.getRowNode(String(0))?.setSelected(true);
              }, 0);
            }}
            onFirstDataRendered={onFirstDataRendered}
            rowData={itens}
            rowHeight={24}
            headerHeight={22}
            rowSelection="single"
            localeText={{
              noRowsToShow: "Não há itens nesta análise",
            }}
            columnDefs={[
              colMestre("Ações", "acoes", 1, "right", {
                minWidth: 64,
                cellRenderer: AcoesCellRenderer,
                cellStyle: {
                  paddingRight: "0px",
                  paddingLeft: "1px",
                  textAlign: "right",
                },
              }),
              colMestre("SKU", "codigo_sku", 2, "right", {
                minWidth: 72,
                cellRenderer: SkuLinkRenderer,
              }),
              colMestre("UN", "ucom", 1, "left", { minWidth: 40 }),
              colMestre("Descrição", "prod_xprod", 8, "left", {
                minWidth: 160,
                tooltipField: "prod_xprod",
              }),
              colMestre("Referência", "referencia", 2.5, "left", {
                tooltipField: "referencia",
              }),
              colMestre("Curva", "curva", 1, "right"),
              colMestre("Marca", "nome_marca", 3, "left", {
                minWidth: 100,
                tooltipField: "nome_marca",
              }),
              colMestre("Tags", "texto_tags", 2, "left", {
                minWidth: getColumnsWidth("Tags0"),

                tooltipField: "texto_tags",
              }),
              colMestre("AC", "codigo_agrup", 0.8, "right"),
              colMestre("Ativo", "ativo", 1, "center", {
                cellRenderer: "agCheckboxCellRenderer",
                cellEditor: "agCheckboxCellEditor",
              }),
              colMestre("TR", "dias_tr", 0.8, "right"),
              colMestre("Situação", "resultado.situacao", 1.5, "center", {
                cellStyle: estiloSituacao,
              }),
              colMestre("NR", "resultado.nr", 1, "right", {
                minWidth: getColumnsWidth("NR"),
              }),
              colMestre("EV", "resultado.ev", 1, "right", {
                minWidth: getColumnsWidth("EV"),
              }),
              colMestre("DNRG", "resultado.dnrg", 1.8, "right", {
                minWidth: getColumnsWidth("dnrg"),
                cellStyle: (params) => ({
                  ...CELL_STYLE.right,
                  paddingRight: "1px",
                  color:
                    params.value > 0
                      ? "blue"
                      : params.value < 0
                        ? "red"
                        : "black",
                }),
              }),
              colMestre("Min Dfx2", "resultado.min_dfx2", 1.5, "right"),
              colMestre("$ Cadastro", "vunit_compra", 2, "right", {
                valueFormatter: formatarBRL,
              }),
              colMestre("% IPI", "aliq_ipi", 1.2, "right", {
                valueFormatter: formatarPercentual,
              }),
              colMestre("Pedido", "quant_pedido", 1.2, "right"),
              colMestre("$ Unit", "preco_unit", 1.5, "right", {
                valueFormatter: formatarBRL,
              }),
              colMestre("$ Total", "total_pedido", 2, "right", {
                valueFormatter: formatarBRL,
              }),
            ]}
          />
        </div>
        <div
          style={{
            height: "5px",
            background: "#ccc",
            cursor: "row-resize",
          }}
          onMouseDown={handleMouseDown}
        />
        {loading ? (
          <Loading
            loadingPrimary="Carregando..."
            loadingSecondary="Por favor, aguarde."
          />
        ) : (
          <>
            {showAlert && !ignoraAlert && (
              <ScmDialogAlert
                mostraDialog={naoSalvo}
                sku={SKU}
                onClose={() => setShowAlert(false)}
                onUndo={() => {
                  setIgnoraAlert(true);
                  ignoraAlertRef.current = true;
                  setShowAlert(false);
                }}
              />
            )}
            <div
              ref={detalheBoxRef}
              className="ag-theme-alpine grid-detalhe"
              style={{
                width: "100%",
              }}
              translate="no"
              onFocus={() => setFocusedGrid("detalhe")}
            >
              <AgGridReact
                ref={detalheGridRef}
                modules={[ClientSideRowModelModule]}
                columnDefs={columnDefs}
                rowData={linhasEmpresa}
                pinnedBottomRowData={linhasFixas}
                getRowId={(params) => params.data.id}
                suppressHorizontalScroll={false}
                domLayout="autoHeight"
                localeText={{
                  noRowsToShow: "Não há dados nesta análise",
                }}
                // Para o foco na linha compras
                onFirstDataRendered={(params) => {
                  const api = params.api;
                  const largura =
                    larguraDetalheRef.current ||
                    detalheBoxRef.current?.clientWidth ||
                    0;
                  aplicarLargurasDetalhe(api, largura);

                  setTimeout(() => {
                    const totalFixas = api.getPinnedBottomRowCount();
                    for (let i = 0; i < totalFixas; i++) {
                      const nome =
                        api.getPinnedBottomRow(i)?.data?.nome_reduzido;
                      if (!nome?.startsWith("Compra")) continue;
                      api.setFocusedCell(i, "qc", "bottom");
                      api.startEditingCell({
                        rowIndex: i,
                        colKey: "qc",
                        rowPinned: "bottom",
                      });
                      break;
                    }
                  }, 50); // pequeno delay para garantir render
                }}
                onGridSizeChanged={(evento) => {
                  larguraDetalheRef.current = evento.clientWidth;
                  requestAnimationFrame(() =>
                    aplicarLargurasDetalhe(
                      evento.api,
                      detalheBoxRef.current?.clientWidth || evento.clientWidth,
                    ),
                  );
                }}
                onGridColumnsChanged={(evento) => {
                  const largura =
                    detalheBoxRef.current?.clientWidth ||
                    larguraDetalheRef.current;
                  requestAnimationFrame(() =>
                    aplicarLargurasDetalhe(evento.api, largura),
                  );
                }}
                onGridReady={() => {
                  onDetalheGridReady();
                  calcularTotal(record?.itens || []);
                  // Sem sizeColumnsToFit: ele espreme tudo na tela e corta os cabeçalhos.
                }}
                rowHeight={24} //altura da linha
                headerHeight={22} //altura cabeçalho
                suppressMovableColumns={true}
                onCellValueChanged={atualizouCelula}
                getRowStyle={(params) => {
                  return {
                    backgroundColor:
                      params?.node?.rowIndex != null &&
                      params.node.rowIndex % 2 === 0
                        ? "#cff6c8"
                        : "#ffffff",
                    // Esse segundo fontWseight tenho que colocar, pq caso eu não informe, ele assume undefined e fica com warning
                    fontWeight: params.node.rowPinned ? "bold" : "normal",
                  };
                }}
                onCellKeyDown={(params) => {
                  const event = params.event as KeyboardEvent;

                  // Linha fixa não avança: o rowIndex dela é o índice entre as
                  // fixas e cairia numa linha de empresa com o mesmo número.
                  if (
                    event.key === "Enter" &&
                    !params.node?.rowPinned &&
                    params.node?.rowIndex != null
                  ) {
                    const nextRowIndex = params.node.rowIndex + 1;
                    const focusedCell = params.api.getFocusedCell();

                    if (
                      focusedCell &&
                      nextRowIndex < params.api.getDisplayedRowCount()
                    ) {
                      // Aguarda um pequeno tempo para garantir que a célula já foi atualizada antes de mudar o foco
                      setTimeout(() => {
                        params.api.startEditingCell({
                          rowIndex: nextRowIndex,
                          colKey: focusedCell.column.getId(),
                        });
                      }, 50);
                    }
                  }
                }}
              />
            </div>
          </>
        )}
        <br /> <br />
      </div>
    </Stack>
  );
};
